import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import HistoryIcon from '@mui/icons-material/History';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
// import WhatshotIcon from '@mui/icons-material/Whatshot'; // Chillies disabled
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import ContentArea from '../components/content-area/Content';
import GatewayCard from '../components/payment/GatewayCard';
import TransactionTable from '../components/payment/TransactionTable';
import DepositsTab from '../components/payment/DepositsTab';
import PaymentIconPng from '../assets/icons/payment.png';
import {
  getPaymentGateways, savePaymentGateway, togglePaymentGateway, getTransactionsGlobal,
} from '../api/paymentApi';
import { useToast } from '../context/ToastContext';
import LocalText from '../components/common/LocalText';
import { useSpaceId } from '../hooks/useSpaceId';
import { usePermissions } from '../hooks/usePermissions';

const GATEWAY_CONFIGS = [
  {
    gatewayKey: 'stripe',
    title: 'Stripe Payments',
    headerBg: '#635bff',
    chipColor: { bg: '#eef2ff', text: '#4f46e5', border: '#c7d2fe' },
    clientIdLabel: 'Live Publishable Key',
    clientIdIcon: <VpnKeyIcon sx={{ fontSize: 14, color: '#94a3b8' }} />,
    secretKeyLabel: 'Live Secret Key',
    secretKeyIcon: <LockOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8' }} />,
    icon: <CreditCardIcon />,
  },
  {
    gatewayKey: 'paypal',
    title: 'PayPal Business',
    headerBg: '#003087',
    chipColor: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
    clientIdLabel: 'Live Client ID',
    clientIdIcon: <BadgeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />,
    secretKeyLabel: 'Live Secret Key',
    secretKeyIcon: <LockOutlinedIcon sx={{ fontSize: 14, color: '#94a3b8' }} />,
    icon: <AccountBalanceWalletIcon />,
  },

];

const GATEWAY_GRID_COLS = { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(auto-fill, minmax(320px, 1fr))' };

const TABS = [
  { key: 'gateways', label: 'Gateways & Config', icon: <TuneIcon sx={{ fontSize: 15 }} /> },
  { key: 'history',  label: 'Transaction History', icon: <HistoryIcon sx={{ fontSize: 15 }} /> },
  { key: 'deposits', label: 'Deposits', icon: <AccountBalanceIcon sx={{ fontSize: 15 }} /> },
];

// ── Page ─────────────────────────────────────────────────────────────────────
const Payments = () => {
  const spaceId = useSpaceId();
  const { canFeature } = usePermissions();
  const canSeeGateways   = canFeature('/payments', 'gateways-tab');
  const canSaveGateway   = canFeature('/payments', 'gateway-save');
  const canToggleGateway = canFeature('/payments', 'gateway-toggle');
  const useGlobalTxApi   = canFeature('/payments', 'transactions-global');
  const canUploadWireDoc = canFeature('/payments', 'wire-doc-upload');

  const [activeTab, setActiveTab]             = useState(canSeeGateways ? 'gateways' : 'history');
  const [gatewayData, setGatewayData]         = useState({});
  const [loadingGateways, setLoadingGateways] = useState(true);
  const { toast } = useToast();

  // Gateways & Config stays finance-only; Transaction History and Deposits are shared
  // between finance and gms_accountant (gms_accountant just fetches the global variant).
  const visibleTabs = TABS.filter((t) => t.key !== 'gateways' || canSeeGateways);

  // ── Load gateways and currency map in parallel on mount ─────────────────
  const loadGateways = async () => {
    try {
      const gateways = await getPaymentGateways();
      const map = {};
      (gateways || []).forEach((g) => { map[g.gatewayKey.toLowerCase()] = g; });
      setGatewayData(map);
    } catch {
      // API not yet available — cards render in unconfigured state
    }
  };

  useEffect(() => {
    if (!spaceId || !canSeeGateways) return;
    setLoadingGateways(true);
    loadGateways().finally(() => setLoadingGateways(false));
  }, [spaceId, canSeeGateways]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save handler — refetches after save so hints update ─────────────────
  const handleGatewaySave = async (gatewayKey, formData) => {
    try {
      await savePaymentGateway(gatewayKey, formData);
      toast.success('Gateway configuration saved successfully.');
      await loadGateways();
    } catch {
      // global interceptor already shows the error toast
    }
  };

  // ── Toggle handler — optimistic update + server sync ────────────────────
  const handleGatewayToggle = async (gatewayKey, isEnabled) => {
    // Optimistic local update
    setGatewayData((prev) => ({
      ...prev,
      [gatewayKey]: prev[gatewayKey]
        ? { ...prev[gatewayKey], isEnabled }
        : null,
    }));
    try {
      await togglePaymentGateway(gatewayKey, isEnabled);
    } catch {
      // Revert optimistic update on failure
      await loadGateways();
    }
  };

  return (
    <ContentArea title="Payments" icon={PaymentIconPng} container={false}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* ── Hero Banner ───────────────────────────────── */}
        <Box
          sx={{
            background: '#1e488f',
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(30,72,143,0.22)',
          }}
        >
          <Box sx={{ position: 'absolute', right: -40, bottom: -40, width: 176, height: 176, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', right: 48, top: 16, width: 112, height: 112, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight={800} sx={{ color: 'white', letterSpacing: '-0.5px', mb: 0.75, fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2.125rem' } }}>
              <LocalText>Payments Control Panel</LocalText>
            </Typography>
            <Typography fontSize={13} sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 620, lineHeight: 1.6 }}>
              <LocalText>Manage payment gateway integrations, configure API credentials, and monitor transaction activity across all portals from a single control panel.</LocalText>
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 4 }}>
              {visibleTabs.map((tab) => {
                const active = activeTab === tab.key;
                return (
                  <Box
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    sx={{
                      px: 2.5, py: 1.25, borderRadius: 2, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 1,
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.08em', transition: 'all 0.18s', userSelect: 'none',
                      bgcolor:   active ? 'white' : 'rgba(255,255,255,0.12)',
                      color:     active ? '#1e3a5f' : 'white',
                      boxShadow: active ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                      '&:hover': { bgcolor: active ? 'white' : 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    {tab.icon}<LocalText>{tab.label}</LocalText>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* ── Gateways Tab ──────────────────────────────── */}
        {activeTab === 'gateways' && (
          loadingGateways ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 10 }}>
              <CircularProgress sx={{ color: '#0a326d' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: GATEWAY_GRID_COLS, gap: 3 }}>
              {GATEWAY_CONFIGS.map((config) => (
                <GatewayCard
                  key={config.gatewayKey}
                  config={config}
                  initialData={gatewayData[config.gatewayKey] ?? null}
                  onSave={handleGatewaySave}
                  onToggle={handleGatewayToggle}
                  canSave={canSaveGateway}
                  canToggle={canToggleGateway}
                />
              ))}
            </Box>
          )
        )}

        {/* ── Transaction History Tab ───────────────────── */}
        {activeTab === 'history' && (
          <TransactionTable
            fetchFn={useGlobalTxApi ? getTransactionsGlobal : undefined}
            showGlobalFilters={useGlobalTxApi}
          />
        )}

        {/* ── Deposits Tab ──────────────────────────────── */}
        {activeTab === 'deposits' && (
          <DepositsTab
            showGlobalFilters={useGlobalTxApi}
            canManageDeposits={canUploadWireDoc}
          />
        )}
      </Box>
    </ContentArea>
  );
};

export default Payments;
